import * as service from "../service";
import type { ServiceResponse } from "../types";

export type GetSponsorsParams = {
  page?: number;
  pageSize?: number;
  /** Exact category to filter by; omit (or pass an empty value) for all. */
  category?: string | null;
};

/**
 * The resolved requirement the API attaches to each task as `requirement`.
 *
 * `followerCount` is resolved server-side from the activity and the declared
 * participant count, so it is passed through verbatim rather than recomputed.
 */
export type SponsorTaskRequirement = {
  /** The declared participant headcount, for a title carrying `{number}`. */
  participants?: number | null;
  followerCount?: number | null;
  followerCountCap?: number | null;
  followTargets?: string[] | null;
  usernameMin?: number | null;
  media?: { min?: number | null; max?: number | null; mime?: string[] | null } | null;
  accountsToRegister?: string[] | null;
};

export type SponsorTaskSummary = {
  id: string;
  title: string;
  /** Kept so the redeem agreement can state the actual obligation. */
  requirement?: SponsorTaskRequirement | null;
};

/**
 * The registered identity of the company that offers a sponsor.
 *
 * Mapped from Prisma's `business_profiles` row, which is why the client sees
 * camelCase where the API sends snake_case.
 */
export type SponsorProviderInfo = {
  legalName?: string | null;
  brNumber?: string | null;
  registeredAddress?: string | null;
  contactNumber?: string | null;
  companyEmail?: string | null;
};

export type SponsorItem = {
  id: string;
  name: string;
  description: string;
  image_url: string;
  price: number | null;
  category: string | null;
  source_url: string | null;
  tasks: SponsorTaskSummary[];
  /**
   * The company behind the sponsor, from the owner's `business_profile`.
   *
   * `name` is a product title, so it cannot identify the counterparty the
   * redemption agreement is made with — a Hong Kong company party is named and
   * identified by its registered name and BR number. Every field is nullable:
   * the row is absent for an owner with no business profile.
   */
  provider?: SponsorProviderInfo | null;
};

/** How many sponsors the server holds per category, for the filter chips. */
export type SponsorCategoryCount = {
  category: string;
  count: number;
};

/** Prisma `Decimal` reaches the browser as a JSON string, so coerce defensively. */
const toPrice = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const price = Number(value);
  return Number.isFinite(price) ? price : null;
};

const toNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const text = String(value);
  return text.length > 0 ? text : null;
};

export type GetSponsorsResult = {
  sponsors: SponsorItem[];
  /** Sponsors matching the CURRENT filter, so the client knows if more pages exist. */
  total: number;
  /** Counts for the whole catalog, never narrowed by `category`. */
  categoryCounts: SponsorCategoryCount[];
};

/**
 * Fetch a page of active, not deleted sponsors (for the redeem centre, not
 * user-specific).
 *
 * Backend: GET /api/sponsor/getSponsors?page&pageSize&category
 * The route answers with an envelope whose `result` is
 * `{ sponsors, total, categoryCounts }`.
 */
export const GetSponsorsAPI = async ({
  page = 1,
  pageSize = 20,
  category,
}: GetSponsorsParams = {}): Promise<ServiceResponse<GetSponsorsResult>> => {
  try {
    const resp = (await service.call("GET", "api/sponsor/getSponsors", {
      page,
      pageSize,
      // An empty category means "no filter", so never send a blank value.
      category: category || undefined,
    })) as ServiceResponse<any>;
    if (resp?.status !== 0 || !Array.isArray(resp.result?.sponsors)) {
      return {
        status: resp?.status ?? -1,
        result: { sponsors: [], total: 0, categoryCounts: [] },
      };
    }
    return {
      status: 0,
      result: {
        total: Number(resp.result.total) || 0,
        categoryCounts: Array.isArray(resp.result.categoryCounts)
          ? resp.result.categoryCounts.map((entry: any) => ({
              category: String(entry?.category ?? ""),
              count: Number(entry?.count) || 0,
            }))
          : [],
        sponsors: resp.result.sponsors.map((sponsor: any, index: number) => ({
          id: String(sponsor?.id ?? index),
          name: String(sponsor?.name ?? ""),
          description: String(sponsor?.description ?? ""),
          image_url: String(sponsor?.image_url ?? ""),
          price: toPrice(sponsor?.price),
          category: toNullableString(sponsor?.category),
          source_url: toNullableString(sponsor?.source_url),
          tasks: Array.isArray(sponsor?.tasks)
            ? sponsor.tasks.map((task: any, taskIndex: number) => ({
                id: String(task?.id ?? taskIndex),
                title: String(task?.title ?? ""),
                requirement: (task?.requirement ?? null) as SponsorTaskRequirement | null,
              }))
            : [],
          provider: (() => {
            const profile = sponsor?.user?.business_profile;
            // `null` rather than a profile of empty strings: the caller tells an
            // absent provider from a provider that has not filled the row in.
            if (!profile || typeof profile !== "object") return null;
            return {
              legalName: toNullableString(profile.company_name),
              brNumber: toNullableString(profile.br_number),
              registeredAddress: toNullableString(profile.company_address),
              contactNumber: toNullableString(profile.contact_number),
              companyEmail: toNullableString(profile.company_email),
            };
          })(),
        })),
      },
    };
  } catch (err) {
    console.error(`GetSponsorsAPI ${err}`);
    return { status: -1, result: { sponsors: [], total: 0, categoryCounts: [] }, error: err };
  }
};